export interface WhiteLabelConfig {
  id: string;
  clientName: string;
  logoUrl: string; // Base64 data URL or empty string
  primaryColor: string; // hex format: #RRGGBB
  secondaryColor: string; // hex format: #RRGGBB
  createdAt: string;
  updatedAt: string;
}

export const DEFAULT_CONFIG: WhiteLabelConfig = {
  id: 'default',
  clientName: 'Kyndly',
  logoUrl: '',
  primaryColor: '#6BC5A7', // seafoam
  secondaryColor: '#0E2C3A', // night
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

const LS_WHITE_LABEL_KEY = 'demo.whiteLabel';
const MAX_LOGO_SIZE = 500 * 1024; // 500KB in bytes

/**
 * White-label configuration store for demo mode.
 * Manages localStorage persistence of branding settings.
 */
export const whiteLabelStore = {
  /**
   * Get the current white-label configuration from localStorage.
   * Returns default config if none exists.
   */
  get(): WhiteLabelConfig {
    const raw = localStorage.getItem(LS_WHITE_LABEL_KEY);
    if (!raw) return DEFAULT_CONFIG;
    try {
      return JSON.parse(raw);
    } catch {
      return DEFAULT_CONFIG;
    }
  },

  /**
   * Save white-label configuration to localStorage.
   * Automatically updates the updatedAt timestamp.
   */
  save(config: WhiteLabelConfig): void {
    const updated = {
      ...config,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(LS_WHITE_LABEL_KEY, JSON.stringify(updated));
    // Trigger storage event for cross-tab sync
    window.dispatchEvent(new Event('whitelabel-update'));
  },

  /**
   * Reset white-label configuration to default Kyndly branding.
   */
  reset(): void {
    localStorage.removeItem(LS_WHITE_LABEL_KEY);
    window.dispatchEvent(new Event('whitelabel-update'));
  },

  /**
   * Validate that a base64 encoded logo is within size limits.
   * @param base64String - Base64 encoded image string
   * @returns true if size is acceptable, false otherwise
   */
  validateLogoSize(base64String: string): boolean {
    // Estimate size: base64 is ~33% larger than binary
    const estimatedSize = (base64String.length * 3) / 4;
    return estimatedSize <= MAX_LOGO_SIZE;
  },

  /**
   * Convert uploaded image file to base64 data URL.
   * @param file - Image file to convert
   * @returns Promise<string> - Base64 encoded data URL
   * @throws Error if file exceeds 500KB
   */
  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      if (file.size > MAX_LOGO_SIZE) {
        reject(new Error(`Logo must be smaller than 500KB (current: ${Math.round(file.size / 1024)}KB)`));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  },
};

/**
 * Check if the application is running in demo mode.
 */
export const isDemoMode = (): boolean => {
  return String(process.env.REACT_APP_ENVIRONMENT).toLowerCase() === 'demo';
};
